import React from 'react'
import ReactDOM from 'react-dom/client'
import { ThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import App from './App'
import { getTenantTheme } from './themes'

declare global {
  interface Window {
    monkeeTranscription?: {
      ajaxUrl: string
      apiBaseUrl: string
      aiBaseUrl?: string
      restUrl: string
      nonce: string
      apiKey: string
      encryptedKey: string
      userId: number
      userEmail: string
      tennantId?: string
      /** Slugs das tags FluentCRM do usuário — usados para resolver limites dinâmicos */
      userTags?: string[]
    }
    MonkeeReactApp?: {
      mount: (selector?: string) => void
    }
  }
}

/**
 * Detecta se está rodando dentro do WordPress
 */
function isWordPressContext(): boolean {
  return !!(
    window.monkeeTranscription?.ajaxUrl ||
    document.querySelector('meta[name="generator"][content*="WordPress"]') ||
    (window as any).wp ||
    (window as any).wpApiSettings
  )
}

/**
 * CSS Reset para isolar o app do WordPress
 * Corrige problemas específicos sem quebrar o MUI
 */
const ISOLATION_STYLES = `
/* ============================================
   MONKEE TRANSCRIPTION APP - CSS ISOLATION v3
   Protege o app React dos estilos do WordPress
   PRESERVA overflow de containers react-window
   ============================================ */

/* CSS Custom Property para backgrounds de Paper/Card.
   inherits: false impede que filhos herdem a cor do pai.
   initial-value: #ffffff garante branco como padrão. */
@property --monkee-bg {
  syntax: '<color>';
  inherits: false;
  initial-value: #ffffff;
}

/* Container principal - reset básico */
#monkee-file-transcript-client,
#monkee-file-transcript-client *,
#monkee-file-transcript-client *::before,
#monkee-file-transcript-client *::after {
  box-sizing: border-box !important;
}

html body #monkee-file-transcript-client {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
  font-size: 14px !important;
  line-height: 1.5 !important;
  color: rgba(0, 0, 0, 0.87) !important;
  background-color: #ffffff !important;
  background: #ffffff !important;
  -webkit-font-smoothing: antialiased !important;
  -moz-osx-font-smoothing: grayscale !important;
}

/* Bloquear background-image de temas WordPress em divs sem classes MUI/Emotion */
#monkee-file-transcript-client div:not([class]),
#monkee-file-transcript-client div[class=""] {
  background-image: none !important;
}

/* ============================================
   ÍCONES SVG - PROTEÇÃO CONTRA RESET DO WP
   WordPress temas frequentemente resetam SVGs.
   NÃO forçar font-size no .MuiSvgIcon-root pois
   componentes usam sx={{ fontSize: { xs, sm } }}
   que geram classes responsivas do MUI.
   ============================================ */
#monkee-file-transcript-client svg:not(.MuiCircularProgress-svg):not(.MuiLinearProgress-bar) {
  fill: currentColor !important;
  vertical-align: middle !important;
  flex-shrink: 0 !important;
}

/* MUI Icons - preservar dimensões relativas a font-size
   NÃO forçar font-size aqui — sx props dos componentes definem 
   valores responsivos que devem prevalecer */
#monkee-file-transcript-client .MuiSvgIcon-root {
  width: 1em !important;
  height: 1em !important;
  fill: currentColor !important;
  display: inline-block !important;
  flex-shrink: 0 !important;
  user-select: none !important;
}

/* Tamanhos somente como fallback, sem !important para não bloquear sx */
#monkee-file-transcript-client .MuiSvgIcon-fontSizeSmall {
  font-size: 1.25rem;
}

#monkee-file-transcript-client .MuiSvgIcon-fontSizeLarge {
  font-size: 2.1875rem;
}

#monkee-file-transcript-client .MuiSvgIcon-fontSizeInherit {
  font-size: inherit !important;
}

/* ============================================
   TABS - CORREÇÃO CRÍTICA DO SCROLL
   O tema WordPress frequentemente aplica overflow:hidden
   que corta as tabs scrollable. Precisamos reverter isso.
   SEM afetar containers de listas virtualizadas (react-window).
   ============================================ */

/* Reset de overflow APENAS para containers de layout principal */
#monkee-file-transcript-client,
#monkee-file-transcript-client > div,
#monkee-file-transcript-client .MuiContainer-root {
  overflow: visible !important;
}

/* MuiBox-root: aplicar overflow visible APENAS quando NÃO tiver
   estilo inline de overflow (react-window define inline) */
#monkee-file-transcript-client .MuiBox-root:not([style*="overflow"]) {
  overflow: visible !important;
}

/* Container principal das Tabs */
#monkee-file-transcript-client .MuiTabs-root {
  overflow: visible !important;
  min-height: 48px !important;
  max-height: 48px !important;
  display: flex !important;
  flex-direction: row !important;
  width: 100% !important;
  background: transparent !important;
  border: none !important;
}

/* Scroller - CRÍTICO para scroll funcionar */
#monkee-file-transcript-client .MuiTabs-scroller {
  overflow-x: scroll !important;
  overflow-y: hidden !important;
  display: flex !important;
  flex: 1 1 auto !important;
  white-space: nowrap !important;
  /* Esconder scrollbar visualmente mas manter funcionalidade */
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

#monkee-file-transcript-client .MuiTabs-scroller::-webkit-scrollbar {
  display: none !important;
  height: 0 !important;
  width: 0 !important;
}

/* Flex container das tabs - inline-flex para não quebrar linha */
#monkee-file-transcript-client .MuiTabs-flexContainer {
  display: inline-flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  gap: 0 !important;
}

/* Scroll buttons (setas) */
#monkee-file-transcript-client .MuiTabs-scrollButtons {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
  width: 40px !important;
  height: 48px !important;
  opacity: 1 !important;
  visibility: visible !important;
}

#monkee-file-transcript-client .MuiTabs-scrollButtons.Mui-disabled {
  opacity: 0.3 !important;
  pointer-events: none !important;
}

#monkee-file-transcript-client .MuiTabs-scrollButtons svg {
  font-size: 1.5rem !important;
  width: 24px !important;
  height: 24px !important;
}

/* Tab individual - proteger layout contra WP sem bloquear estilos sx */
#monkee-file-transcript-client .MuiTab-root {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
  /* NÃO forçar text-transform — MUI define uppercase por padrão,
     componentes podem sobrescrever via sx (textTransform: 'none') */
  overflow: visible !important;
  white-space: nowrap !important;
  flex-shrink: 0 !important;
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0 !important;
  opacity: 1 !important;
  /* NÃO forçar font-size, font-weight, min-height, max-height, padding
     — componentes definem valores responsivos via sx que devem prevalecer */
  background: transparent !important;
  border: none !important;
  border-radius: 0 !important;
  text-decoration: none !important;
  text-align: center !important;
  vertical-align: middle !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  box-shadow: none !important;
}

/* Label da tab */
#monkee-file-transcript-client .MuiTab-root .MuiTab-wrapper,
#monkee-file-transcript-client .MuiTab-root > span:not(.MuiTouchRipple-root),
#monkee-file-transcript-client .MuiTab-root > div {
  white-space: nowrap !important;
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 4px !important;
  overflow: visible !important;
  font-size: inherit !important;
  line-height: inherit !important;
  color: inherit !important;
  text-decoration: none !important;
}

/* Ícones dentro das tabs */
#monkee-file-transcript-client .MuiTab-root .MuiSvgIcon-root {
  font-size: 1.25rem !important;
  margin-right: 6px !important;
  flex-shrink: 0 !important;
}

/* Tab indicator */
#monkee-file-transcript-client .MuiTabs-indicator {
  height: 2px !important;
  bottom: 0 !important;
  display: block !important;
  visibility: visible !important;
  background-color: #1976d2 !important;
}

/* ButtonBase dentro de tabs - reset completo do tema WP */
#monkee-file-transcript-client .MuiTab-root.MuiButtonBase-root {
  flex-direction: row !important;
  gap: 0 !important;
  border: none !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  text-decoration: none !important;
  outline: none !important;
  -webkit-tap-highlight-color: transparent !important;
}

/* ============================================
   BOTÕES - PRESERVAR ESTILOS MUI
   NÃO resetar background-image: impede gradientes
   definidos via sx={{ backgroundImage: 'linear-gradient(...)' }}
   NÃO resetar text-transform: MUI usa uppercase por
   padrão; componentes que querem none definem via sx.
   ============================================ */
#monkee-file-transcript-client button:not(.MuiButton-root) {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
  cursor: pointer !important;
  background-image: none !important;
  text-decoration: none !important;
  letter-spacing: normal !important;
  word-spacing: normal !important;
  text-indent: 0 !important;
  text-shadow: none !important;
  text-rendering: auto !important;
}

#monkee-file-transcript-client .MuiButton-root {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
  /* text-transform: deixar o MUI controlar (uppercase padrão) */
  line-height: 1.75 !important;
  letter-spacing: 0.02857em !important;
  cursor: pointer !important;
  text-decoration: none !important;
  text-indent: 0 !important;
  text-shadow: none !important;
  text-rendering: auto !important;
}

#monkee-file-transcript-client .MuiIconButton-root {
  padding: 8px !important;
  border-radius: 50% !important;
}

/* ============================================
   INPUTS / TEXTFIELDS - PRESERVAR BORDAS MUI
   ============================================ */
#monkee-file-transcript-client input:not([type="checkbox"]):not([type="radio"]),
#monkee-file-transcript-client textarea {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
  font-size: 1rem !important;
  line-height: 1.4375em !important;
  color: inherit !important;
  background: transparent !important;
  outline: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
}

#monkee-file-transcript-client input[type="file"] {
  display: none !important;
}

/* Input dentro do MUI TextField - SEM border reset */
#monkee-file-transcript-client .MuiInputBase-input {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
  padding: 16.5px 14px !important;
}

/* Borda do OutlinedInput - PRESERVAR */
#monkee-file-transcript-client .MuiOutlinedInput-root {
  border-radius: 4px !important;
}

#monkee-file-transcript-client .MuiOutlinedInput-notchedOutline {
  border: 1px solid rgba(0, 0, 0, 0.23) !important;
  border-radius: 4px !important;
}

#monkee-file-transcript-client .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
  border-color: rgba(0, 0, 0, 0.87) !important;
}

#monkee-file-transcript-client .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
  border-color: #1976d2 !important;
  border-width: 2px !important;
}

/* ============================================
   TIPOGRAFIA - ISOLAÇÃO MUI
   Protege font-family contra WordPress.
   NÃO força font-size/weight/line-height pois
   isso sobrescreveria os estilos sx dos componentes.
   ============================================ */
#monkee-file-transcript-client h1,
#monkee-file-transcript-client h2,
#monkee-file-transcript-client h3,
#monkee-file-transcript-client h4,
#monkee-file-transcript-client h5,
#monkee-file-transcript-client h6,
#monkee-file-transcript-client p {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
  margin: 0 !important;
  padding: 0 !important;
  color: inherit !important;
  text-decoration: none !important;
  /* NÃO forçar text-align, font-size, line-height - deixar MUI/sx controlar */
}

#monkee-file-transcript-client span,
#monkee-file-transcript-client label,
#monkee-file-transcript-client legend {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
  margin: 0 !important;
  color: inherit !important;
  text-transform: inherit !important;
  text-decoration: none !important;
}

/* MUI Typography base - proteger font-family */
#monkee-file-transcript-client .MuiTypography-root {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
  word-spacing: normal !important;
}

/* Chips - proteger font-family apenas */
#monkee-file-transcript-client .MuiChip-root {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
}

/* Centralização quando explicitamente definida */
#monkee-file-transcript-client [style*="text-align: center"],
#monkee-file-transcript-client [style*="text-align:center"],
#monkee-file-transcript-client .MuiTypography-align-center {
  text-align: center !important;
}

/* ============================================
   LINKS
   ============================================ */
#monkee-file-transcript-client a {
  color: inherit !important;
  text-decoration: none !important;
}

/* ============================================
   LISTAS
   ============================================ */
#monkee-file-transcript-client ul,
#monkee-file-transcript-client ol {
  list-style: none !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* ============================================
   PAPER / CARDS — proteger contra tema WordPress
   mas SEM !important no background para permitir
   que sx props (bgcolor, background) prevaleçam.
   Emotion styles com prepend:false já vencem o WP
   na cascade. Usar !important somente na color.
   ============================================ */
#monkee-file-transcript-client .MuiPaper-root,
#monkee-file-transcript-client .MuiCard-root {
  color: rgba(0, 0, 0, 0.87) !important;
}

/* Background default: usar especificidade alta MAS sem !important 
   para que sx={{ bgcolor: ... }} (inline Emotion) vença */
html body #monkee-file-transcript-client#monkee-file-transcript-client .MuiPaper-root:not([style*="background"]),
html body #monkee-file-transcript-client#monkee-file-transcript-client .MuiCard-root:not([style*="background"]) {
  background-color: var(--monkee-bg, #ffffff);
  background: var(--monkee-bg, #ffffff);
}

/* CardContent default */
html body #monkee-file-transcript-client#monkee-file-transcript-client .MuiCardContent-root:not([style*="background"]) {
  background-color: var(--monkee-bg, #ffffff);
  background: var(--monkee-bg, #ffffff);
}

/* ============================================
   CHECKBOX / RADIO
   ============================================ */
#monkee-file-transcript-client .MuiCheckbox-root svg,
#monkee-file-transcript-client .MuiRadio-root svg {
  font-size: 24px !important;
}

/* ============================================
   SWITCH — reset WordPress checkbox interference
   MUI Switch usa um <input type="checkbox"> com
   position:absolute, width:100%, height:100%, opacity:0
   que serve de hit area. O WordPress reduz width/height
   e aplica appearance, quebrando o clique.
   ============================================ */
#monkee-file-transcript-client .MuiSwitch-root,
.MuiDialog-root .MuiSwitch-root,
.MuiModal-root .MuiSwitch-root {
  position: relative !important;
  display: inline-flex !important;
  width: 58px !important;
  height: 38px !important;
  padding: 12px !important;
  overflow: visible !important;
  box-sizing: border-box !important;
  cursor: pointer !important;
}

#monkee-file-transcript-client .MuiSwitch-root input[type="checkbox"],
.MuiDialog-root .MuiSwitch-root input[type="checkbox"],
.MuiModal-root .MuiSwitch-root input[type="checkbox"] {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  opacity: 0 !important;
  cursor: inherit !important;
  z-index: 1 !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  box-sizing: border-box !important;
}

#monkee-file-transcript-client .MuiSwitch-switchBase,
.MuiDialog-root .MuiSwitch-switchBase,
.MuiModal-root .MuiSwitch-switchBase {
  position: absolute !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-sizing: border-box !important;
  padding: 9px !important;
  z-index: 1 !important;
  transition: left 150ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms cubic-bezier(0.4, 0, 0.2, 1) !important;
}

#monkee-file-transcript-client .MuiSwitch-track,
.MuiDialog-root .MuiSwitch-track,
.MuiModal-root .MuiSwitch-track {
  width: 100% !important;
  height: 100% !important;
  border-radius: 7px !important;
  box-sizing: border-box !important;
  z-index: 0 !important;
}

#monkee-file-transcript-client .MuiSwitch-thumb,
.MuiDialog-root .MuiSwitch-thumb,
.MuiModal-root .MuiSwitch-thumb {
  width: 20px !important;
  height: 20px !important;
  border-radius: 50% !important;
  box-shadow: 0 2px 1px -1px rgba(0,0,0,0.2), 0 1px 1px 0 rgba(0,0,0,0.14), 0 1px 3px 0 rgba(0,0,0,0.12) !important;
}

/* ============================================
   DIALOGS / MODALS / MENUS (PORTALS)
   ============================================ */
.MuiDialog-root,
.MuiModal-root,
.MuiPopover-root,
.MuiMenu-root,
.MuiPopper-root {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
}

.MuiDialog-paper,
.MuiPopover-paper,
.MuiMenu-paper {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
  font-size: 14px !important;
  line-height: 1.5 !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

/* Typography variants dentro de portals */
.MuiDialog-root .MuiTypography-root,
.MuiModal-root .MuiTypography-root {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
}

/* Menu items em portals */
.MuiMenu-root .MuiMenuItem-root {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
}

/* SVGs em portals */
.MuiDialog-root .MuiSvgIcon-root,
.MuiModal-root .MuiSvgIcon-root,
.MuiPopover-root .MuiSvgIcon-root,
.MuiMenu-root .MuiSvgIcon-root,
.MuiPopper-root .MuiSvgIcon-root {
  width: 1em !important;
  height: 1em !important;
  font-size: 1.5rem !important;
  fill: currentColor !important;
}

/* Inputs em portals */
.MuiDialog-root .MuiOutlinedInput-notchedOutline,
.MuiModal-root .MuiOutlinedInput-notchedOutline,
.MuiPopover-root .MuiOutlinedInput-notchedOutline {
  border: 1px solid rgba(0, 0, 0, 0.23) !important;
}

/* Botões em portals — NÃO resetar text-transform nem background-image
   para preservar estilos MUI (uppercase, gradientes) */
.MuiDialog-root button,
.MuiModal-root button,
.MuiPopover-root button,
.MuiMenu-root button {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
}

/* ============================================
   TOOLTIPS
   ============================================ */
.MuiTooltip-tooltip {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
  font-size: 0.6875rem !important;
}

/* ============================================
   PROGRESS INDICATORS
   ============================================ */
#monkee-file-transcript-client .MuiCircularProgress-root {
  display: inline-block !important;
}

#monkee-file-transcript-client .MuiCircularProgress-svg {
  display: block !important;
}

#monkee-file-transcript-client .MuiLinearProgress-root {
  overflow: hidden !important;
}

/* ============================================
   FAB
   ============================================ */
#monkee-file-transcript-client .MuiFab-root {
  border-radius: 50% !important;
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
  cursor: pointer !important;
  text-decoration: none !important;
  text-shadow: none !important;
  text-indent: 0 !important;
  letter-spacing: normal !important;
  text-rendering: auto !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
}

#monkee-file-transcript-client .MuiFab-root.MuiFab-extended {
  border-radius: 24px !important;
}

#monkee-file-transcript-client .MuiFab-root .MuiSvgIcon-root {
  font-size: 24px !important;
}

/* ============================================
   ALERTS
   ============================================ */
#monkee-file-transcript-client .MuiAlert-root {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
}

#monkee-file-transcript-client .MuiAlert-icon .MuiSvgIcon-root {
  font-size: 22px !important;
}

/* ============================================
   BREADCRUMBS
   ============================================ */
#monkee-file-transcript-client .MuiBreadcrumbs-separator .MuiSvgIcon-root {
  font-size: 20px !important;
}

/* ============================================
   CHIPS
   ============================================ */
#monkee-file-transcript-client .MuiChip-root {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
}

#monkee-file-transcript-client .MuiChip-icon,
#monkee-file-transcript-client .MuiChip-deleteIcon {
  font-size: 18px !important;
}

/* ============================================
   ACCORDION
   ============================================ */
html body #monkee-file-transcript-client#monkee-file-transcript-client .MuiAccordion-root:not([style*="background"]) {
  background-color: var(--monkee-bg, #ffffff);
  background: var(--monkee-bg, #ffffff);
}

#monkee-file-transcript-client .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root {
  font-size: 24px !important;
}

/* ============================================
   SNACKBAR
   ============================================ */
.MuiSnackbar-root {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
}

.MuiSnackbar-root .MuiSvgIcon-root {
  width: 1em !important;
  height: 1em !important;
  font-size: 1.5rem !important;
  fill: currentColor !important;
}

/* ============================================
   DRAWER
   ============================================ */
.MuiDrawer-root,
.MuiDrawer-paper {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
}

.MuiDrawer-root .MuiSvgIcon-root,
.MuiDrawer-paper .MuiSvgIcon-root {
  width: 1em !important;
  height: 1em !important;
  font-size: 1.5rem !important;
}

/* ============================================
   TABLES
   ============================================ */
#monkee-file-transcript-client table {
  border-collapse: collapse !important;
  border-spacing: 0 !important;
}

#monkee-file-transcript-client th,
#monkee-file-transcript-client td {
  font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
}

/* ============================================
   IMAGES
   ============================================ */
#monkee-file-transcript-client img {
  max-width: 100% !important;
  height: auto !important;
  vertical-align: middle !important;
}

/* ============================================
   BOX FLEX / ALIGNMENT FIXES
   ============================================ */

/* Garantir que text-align center funcione */
#monkee-file-transcript-client [class*="MuiBox"][style*="text-align"] {
  text-align: inherit !important;
}

/* Reset específico para containers de layout principal */
#monkee-file-transcript-client .MuiContainer-root {
  overflow: visible !important;
}

/* MuiBox-root direto do container principal - overflow visible
   (mas não todos os MuiBox-root, para não quebrar react-window) */
#monkee-file-transcript-client > .MuiBox-root:not([style*="overflow"]) {
  overflow: visible !important;
}
`

/**
 * Injeta ou atualiza o favicon do tenant no documento
 */
function injectFavicon(type: 'emoji' | 'image', value: string, tenantId?: string): void {
  const existingLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  const link = existingLink ?? document.createElement('link')
  link.rel = 'icon'

  if (type === 'image') {
    const src = value
    link.type = 'image/x-icon'
    link.href = src
  } else {
    // Favicon via SVG com emoji (sem servidor necessário)
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="90">${value}</text></svg>`
    link.type = 'image/svg+xml'
    link.href = `data:image/svg+xml,${encodeURIComponent(svg)}`
  }

  if (!existingLink) {
    document.head.appendChild(link)
  }
}

/**
 * Injeta os estilos de isolamento no documento
 */
function injectIsolationStyles(): void {
  const styleId = 'monkee-transcription-isolation-styles'

  // Evitar duplicação
  if (document.getElementById(styleId)) {
    return
  }

  const styleElement = document.createElement('style')
  styleElement.id = styleId
  styleElement.textContent = ISOLATION_STYLES

  // Inserir no head com alta prioridade (no final para sobrescrever)
  document.head.appendChild(styleElement)

  // Também carregar a fonte Roboto se não estiver presente
  if (!document.querySelector('link[href*="fonts.googleapis.com/css"][href*="Roboto"]')) {
    const fontLink = document.createElement('link')
    fontLink.rel = 'stylesheet'
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap'
    document.head.appendChild(fontLink)
  }
}

/**
 * Monta o React App com isolamento CSS
 */
function mountApp(container: HTMLElement) {
  const isWP = isWordPressContext()

  // Setar background inline no container (inline style tem prioridade máxima)
  if (isWP) {
    container.style.setProperty('background-color', '#ffffff', 'important')
    container.style.setProperty('background', '#ffffff', 'important')
  }

  // Configurar Emotion cache
  // Em WordPress: prepend: false coloca Emotion styles DEPOIS do CSS do tema WordPress
  // no <head>, garantindo que MUI/Emotion tenha prioridade maior e não seja
  // sobrescrito por regras de tipografia do WordPress (.entry-content p, etc.).
  // ISOLATION_STYLES (injetado depois via requestAnimationFrame) usa !important
  // e alta especificidade, vencendo tanto WordPress quanto Emotion.
  // Em standalone: prepend não importa pois não há CSS conflitante.
  const emotionCache = createCache({
    key: 'monkee',
    prepend: false,
  })

  // Resolver tema do tenant
  const tenantId = import.meta.env.VITE_APP_TENNANT ||
    (window as any).monkeeTranscription?.tennantId
  const tenantTheme = getTenantTheme(tenantId)

  // Injetar favicon dinamicamente
  injectFavicon(tenantTheme.favicon.type, tenantTheme.favicon.value, tenantId)

  // Atualizar título da aba
  if (!isWP) {
    document.title = tenantTheme.appTitle
  }

  // Criar tema MUI com cores do tenant + overrides para WordPress
  const theme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: tenantTheme.palette.primary,
        ...(tenantTheme.palette.primaryDark ? { dark: tenantTheme.palette.primaryDark } : {}),
      },
      ...(tenantTheme.palette.secondary ? { secondary: { main: tenantTheme.palette.secondary } } : {}),
      background: {
        default: '#ffffff',
        paper: '#ffffff',
      },
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    ...(isWP ? {
      components: {
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundColor: '#ffffff',
              background: '#ffffff',
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: '#ffffff',
              background: '#ffffff',
            },
          },
        },
        MuiCardContent: {
          styleOverrides: {
            root: {
              backgroundColor: '#ffffff',
              background: '#ffffff',
            },
          },
        },
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              // Não deixar CssBaseline resetar backgrounds
            },
          },
        },
      },
    } : {}),
  })

  // Renderizar
  const root = ReactDOM.createRoot(container)
  root.render(
    <CacheProvider value={emotionCache}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <App config={window.monkeeTranscription} />
        </ThemeProvider>
      </StyledEngineProvider>
    </CacheProvider>
  )

  // Injetar ISOLATION_STYLES DEPOIS do React render para que fiquem
  // DEPOIS dos styles do Emotion/MUI no <head>, garantindo cascade correto.
  if (isWP) {
    requestAnimationFrame(() => {
      injectIsolationStyles()
      console.log('[MonkeeReactApp] CSS isolation styles injected AFTER render (v4)')
    })
  }

  console.log(`[MonkeeReactApp] Mounted ${isWP ? 'in WordPress with CSS isolation' : 'in standalone mode'}`)
}

/**
 * Função principal de montagem
 */
function mount(selector = '#monkee-file-transcript-client') {
  // Tentar encontrar o container
  let container = document.querySelector(selector) as HTMLElement | null

  // Fallback
  if (!container) {
    container = document.getElementById('monkee-transcription-root') as HTMLElement | null
  }

  // Se ainda não encontrou, criar
  if (!container) {
    container = document.createElement('div')
    container.id = 'monkee-file-transcript-client'
    document.body.appendChild(container)
  }

  // Verificar se já foi montado (evita duplicação)
  if (container.hasAttribute('data-monkee-mounted')) {
    console.log('[MonkeeReactApp] Already mounted, skipping')
    return
  }

  // Marcar como montado
  container.setAttribute('data-monkee-mounted', 'true')

  // Montar o app
  mountApp(container)
}

// Auto-mount se container existir
const autoMountContainer = document.getElementById('monkee-file-transcript-client') ||
                           document.getElementById('monkee-transcription-root')
if (autoMountContainer) {
  mount('#' + autoMountContainer.id)
}

// Expor para WordPress chamar após enqueue
window.MonkeeReactApp = { mount }
